import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useEffect,
} from 'react';
import { storageTrailKey, storageTrailIdx } from '../utils/consts';

// Initial state restored from sessionStorage (per-tab persistence)
const defaultState = (() => {
  let timeline = [];
  let currentIdx = 0;
  try {
    const raw = sessionStorage.getItem(storageTrailKey);
    if (raw) timeline = JSON.parse(raw);
    const idxRaw = sessionStorage.getItem(storageTrailIdx);
    if (idxRaw) currentIdx = JSON.parse(idxRaw);
  } catch (_) {}

  return { timeline, currentIdx };
})();

// Context shape:
// - timeline: the full navigation log
// - currentIdx: current browser history index (window.history.state.idx)
// - trail: FULL "visible up to currentIdx"
// - actions: reducer commands to mutate the store
const TrailCtx = createContext({
  timeline: /** @type {Array<{idx:number,url:string}>} */ ([]),
  currentIdx: 0,
  trail: /** @type {Array<{idx:number,url:string}>} */ ([]),
  actions: {
    resetTo: (_idx, _url) => {},
    push: (_idx, _url) => {},
    replace: (_idx, _url) => {},
    popTo: (_idx) => {},
    clear: () => {},
  },
});

function reducer(state, action) {
  switch (action.type) {
    // Replace the entire session with a single root record
    // Used when landing on a "home" path (clean slate)
    case 'RESET_TO': {
      const { idx, url } = action;
      const next = [{ idx, url }];
      return { ...state, timeline: next, currentIdx: idx };
    }

    // Forward navigation:
    // 1) cut off any "future" branch if we previously went Back (i.idx > currentIdx)
    // 2) avoid duplicating the last URL
    // 3) append the new record and move currentIdx
    case 'PUSH': {
      const { idx, url } = action;
      const base = state.timeline.filter((i) => i.idx <= state.currentIdx);
      const last = base[base.length - 1];
      const appended =
        last && last.url === url ? base : [...base, { idx, url }];
      return { ...state, timeline: appended, currentIdx: idx };
    }

    // Replace current entry in place (same idx), e.g. Router REPLACE
    case 'REPLACE': {
      const { idx, url } = action;
      const next = state.timeline.slice();
      const pos = next.findIndex((i) => i.idx === idx);
      if (pos >= 0) next[pos] = { idx, url };
      else next.push({ idx, url }); // safety net if missing
      return { ...state, timeline: next, currentIdx: idx };
    }

    // Back/Forward (POP): do NOT mutate timeline, only move the pointer
    case 'POP_TO': {
      const { idx } = action;
      return { ...state, currentIdx: idx };
    }

    // Hard reset (e.g., manual clear)
    case 'CLEAR':
      return { ...state, timeline: [], currentIdx: 0 };

    default:
      return state;
  }
}

export default function TrailProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, defaultState);

  // Persist to sessionStorage after every change
  useEffect(() => {
    try {
      sessionStorage.setItem(storageTrailKey, JSON.stringify(state.timeline));
      sessionStorage.setItem(storageTrailIdx, JSON.stringify(state.currentIdx));
    } catch (_) {}
  }, [state.timeline, state.currentIdx]);

  // FULL visible trail (no display limit): only records at or before currentIdx
  const trail = useMemo(() => {
    return state.timeline.filter((i) => i.idx <= state.currentIdx);
  }, [state.timeline, state.currentIdx]);

  const actions = useMemo(
    () => ({
      resetTo: (idx, url) => dispatch({ type: 'RESET_TO', idx, url }),
      push: (idx, url) => dispatch({ type: 'PUSH', idx, url }),
      replace: (idx, url) => dispatch({ type: 'REPLACE', idx, url }),
      popTo: (idx) => dispatch({ type: 'POP_TO', idx }),
      clear: () => dispatch({ type: 'CLEAR' }),
    }),
    []
  );

  return (
    <TrailCtx.Provider
      value={{
        timeline: state.timeline,
        currentIdx: state.currentIdx,
        trail,
        actions,
      }}
    >
      {children}
    </TrailCtx.Provider>
  );
}

export const useTrail = () => useContext(TrailCtx);
